import * as Sentry from "@sentry/react"
import { StateCreator } from "zustand"
import { FiltroEntregaRetiradaEnum } from "../pages/RecebedorDePedidos/components/FiltroEntregaRetirada"
import { verificarPrintQRCode } from "../pages/RecebedorDePedidos/functions"
import { imprimirNotaFiscal, imprimirNotaGeral } from "../pages/RecebedorDePedidos/qzConfig"
import { RegistrarLogErro } from "../services/clientefiel"
import {
    AceitarPedido,
    buscarInformacoesPedido,
    CancelarPedido,
    DespacharPedido,
    FinalizarPedido,
    impressoesPendentes,
    PrepararPedido,
} from "../services/integracao"
import { alertStart } from "../routes"
import api from "../services/api"
import { StoreType } from "./AppStore"
import { marcarResyncResolvido, resyncJaResolvido } from "./ImpressaoStore"
import { anunciarPedidoAtualizado } from "./canalPedidos"
import { autoAceiteLigado } from "./ConfigsStore"
import useLoadingStore from "./LoadingStore"
import { TabStatusEnum } from "./TabStatusStore"

// VIEW e GARÇOM são modos PASSIVOS desta estação: NUNCA aceitam nem imprimem. VIEW só
// acompanha; GARÇOM é o terminal de lançamento do celular (nem assina o SSE). Os gates de
// aceite/impressão que antes checavam só VIEW passam a cobrir os dois.
export const ehModoPassivo = (tab: TabStatusEnum): boolean =>
    tab === TabStatusEnum.VIEW || tab === TabStatusEnum.GARCOM

// Rede de segurança (redeSegurancaPedidos): intervalo do polling allday e janela de
// reimpressão dos pedidos recentes. Antes viviam no RecebedorDePedidos; agora ficam no
// store para a rede rodar globalmente (a partir do Cabeçalho, em qualquer tela).
// Padrão de 60s (1 min).
export const INTERVALO_REDE_SEGURANCA_MS = 60000

// Piso de segurança para o override: nunca pollar o allday mais rápido que isto, para
// não martelar o backend (cada aba em operação faz sua própria chamada por ciclo).
const INTERVALO_REDE_SEGURANCA_MIN_MS = 15000

// Intervalo EFETIVO do polling allday. Ajustável sem novo deploy por
// localStorage["intervaloRedeSegurancaSeg"] (em SEGUNDOS): valor válido e >= 15s vale;
// ausente/ inválido cai no padrão de 60s. Lido quando o intervalo é criado (escopo de
// módulo), então uma mudança só passa a valer após recarregar a página.
export function getIntervaloRedeSegurancaMs(): number {
    try {
        const bruto = localStorage.getItem("intervaloRedeSegurancaSeg")
        if (bruto && String(bruto).trim()) {
            const segundos = Number(bruto)
            if (Number.isFinite(segundos) && segundos > 0) {
                return Math.max(INTERVALO_REDE_SEGURANCA_MIN_MS, Math.round(segundos * 1000))
            }
        }
    } catch {
        /* ignore */
    }
    return INTERVALO_REDE_SEGURANCA_MS
}

const JANELA_REIMPRESSAO_MIN = 10

// Aviso (com throttle) de que o QZ Tray está desconectado e a comanda não saiu.
// Evita spam quando vários pedidos são aceitos em sequência (aceitarPedidosAutomaticos).
const avisarQzDesconectado = () => {
    try {
        const agora = Date.now()
        const ultimo = Number(localStorage.getItem("ultimoAvisoQz") || 0)
        if (agora - ultimo < 30000) return
        localStorage.setItem("ultimoAvisoQz", String(agora))
    } catch {
        /* ignore */
    }
    alertStart(
        "Impressora (QZ Tray) desconectada — o pedido foi aceito, mas a comanda não foi impressa. Verifique o QZ Tray.",
        "warning"
    )
}

// Corrida multi-estação no aceite/preparo: quando duas estações confirmam o MESMO
// pedido quase ao mesmo tempo, a perdedora perde o lock otimista (@Version) no commit
// e o backend responde SUCESSO com body VAZIO (sem id). Não é falha — a ação foi
// efetivada pela OUTRA estação. Reconcilia a lista e imprime a via DESTA estação
// (dedup por navegador) quando o pedido já está aceito/em preparo. Retorna true se
// tratou como corrida concorrente; false se for falha real (segue aguardando / sumiu).
//
// A reconsulta é RETENTADA: logo após perder a corrida, o commit do vencedor pode
// ainda não ter propagado e o pedido vir em status 0 — o que faria a perdedora exibir
// um popup de erro para uma corrida benigna. Só concluímos "falha real" se, após
// algumas tentativas curtas, o pedido seguir em status 0.
const RECONCILIAR_TENTATIVAS = 3
const RECONCILIAR_DELAY_MS = 1000
async function reconciliarAcaoConcorrente(
    get: () => StoreType,
    pedido: any,
    imprimir: boolean,
    podeImprimir: boolean
): Promise<boolean> {
    try {
        let atual: any = null
        for (let tentativa = 0; tentativa < RECONCILIAR_TENTATIVAS; tentativa++) {
            atual = await buscarInformacoesPedido(
                localStorage.getItem("chaveSistema"),
                pedido.correlationId || pedido.id
            )
            if (atual?.id && atual.status !== 0) break // já avançado por outra estação
            if (tentativa < RECONCILIAR_TENTATIVAS - 1)
                await new Promise(r => setTimeout(r, RECONCILIAR_DELAY_MS))
        }
        if (!atual?.id || atual.status === 0) return false
        // Upsert (remove de TODAS as listas por id e reinsere na certa) em vez de
        // remover-da-anterior + adicionar. O par antigo removia pela lista do status VELHO
        // que veio no `pedido`: se nesse meio tempo outro caminho já tivesse reconciliado o
        // pedido (evento leve de STATUS, rede de segurança), a remoção não achava nada e a
        // inserção criava um SEGUNDO card do mesmo pedido na coluna. A janela é larga aqui
        // — a reconsulta espera até 3s pelo commit de quem venceu a corrida.
        get().upsertPedidoNasListas(atual)
        if (imprimir && podeImprimir && [1, 2, 7].includes(atual.status))
            get().imprimirComandaEstacaoSePendente(atual)
        return true
    } catch {
        return false
    }
}

// ── Reivindicação atômica entre abas (check-and-set por navegador) ──
// Fábrica de um "reivindicador": guarda ids já tratados em localStorage (janela de
// expiração) e serializa o check-and-set num Web Lock (`navigator.locks`, o mesmo
// árbitro entre abas que elege o líder do SSE). Retorna true só para a PRIMEIRA
// aba/handler a reivindicar o id; os demais recebem false.
//
// Sem o lock, quando a aba líder faz o fan-out do evento via BroadcastChannel todas
// as abas leem "não processado" ANTES de qualquer uma marcar (corrida TOCTOU) e cada
// uma dispara a ação — N abas → N vias (impressão) / N aceites concorrentes.
// Reivindicador: função de reivindicação + um método `liberar` que devolve um id à
// janela. Usado quando a AÇÃO não se concretizou (ou avançou só parcialmente): sem
// liberar, a marca otimista seguraria o id por toda a janela e a próxima tentativa
// cairia em "já tratado (pulado)".
type Reivindicador = ((id: number | string) => Promise<boolean>) & {
    liberar: (id: number | string) => void
}

function criarReivindicador(lsKey: string, janelaMs: number, lockPrefix: string): Reivindicador {
    const ler = (): Record<string, number> => {
        try {
            return JSON.parse(localStorage.getItem(lsKey) || "{}")
        } catch {
            return {}
        }
    }
    const marcar = (id: number | string) => {
        try {
            const mapa = ler()
            const agora = Date.now()
            mapa[String(id)] = agora
            for (const k of Object.keys(mapa)) {
                if (agora - mapa[k] > janelaMs) delete mapa[k]
            }
            localStorage.setItem(lsKey, JSON.stringify(mapa))
        } catch {
            /* ignore */
        }
    }
    const liberar = (id: number | string) => {
        try {
            const mapa = ler()
            delete mapa[String(id)]
            localStorage.setItem(lsKey, JSON.stringify(mapa))
        } catch {
            /* ignore */
        }
    }
    const decidir = (id: number | string): boolean => {
        const t = ler()[String(id)]
        if (t != null && Date.now() - t < janelaMs) return false
        marcar(id)
        return true
    }
    const reivindicar = (async (id: number | string): Promise<boolean> => {
        const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined
        if (!locks?.request) return decidir(id) // fallback best-effort (sem Web Locks)
        try {
            return await locks.request(
                `${lockPrefix}:${String(id)}`,
                { mode: "exclusive" },
                () => decidir(id)
            )
        } catch {
            return decidir(id)
        }
    }) as Reivindicador
    reivindicar.liberar = liberar
    return reivindicar
}

// Dedup de IMPRESSÃO por SolicitacaoImpressao.id: chave estável entre o push SSE e o
// drain da fila (e entre PRINT1/PRINT2 do mesmo pedido). Janela de 15min, igual à da
// fila do backend. Por navegador → cada estação/navegador imprime 1x.
const reivindicarImpressao = criarReivindicador("impressoesProcessadas", 15 * 60 * 1000, "impressao")

// Dedup do ACEITE/preparo automático por pedido: o PLACED do SSE é processado por
// MAIS DE UM assinante no mesmo navegador (o Cabeçalho e a própria tela do Recebedor)
// e por várias abas — sem isso, cada um dispararia aceitarPedido/prepararPedido para
// o MESMO pedido, gerando aceites concorrentes (o perdedor recebe body vazio) e vias
// extras. Só UMA reivindicação vence por navegador. Janela curta (60s): se o aceite
// falhar de fato, a rede-de-segurança do Recebedor (resync por minuto) e o drain da
// fila reofertam o pedido depois.
const reivindicarAceite = criarReivindicador("aceitesProcessados", 60 * 1000, "aceite")

// Dedup do PREPARO automático por pedido, em balde SEPARADO do aceite. Aceitar e
// preparar são ações diferentes do MESMO pedido; se dividissem a mesma marca, o aceite
// "consumiria" a reivindicação e o preparo seguinte seria pulado ("já tratado"),
// deixando o pedido preso em "Aceito". Mesma janela de 60s.
const reivindicarPreparo = criarReivindicador("preparosProcessados", 60 * 1000, "preparo")

// Dedup da IMPRESSÃO DA COMANDA por pedido: TODOS os caminhos que imprimem a comanda
// (aceite, preparo e a via de estação da corrida) passam por aqui, então o pedido sai
// no máximo 1x por navegador — mesmo quando o aceite é disparado por rotas diferentes
// (PLACED do SSE, aceitarPedidosAutomaticos do refresh, else-if de estação). Substitui
// o `imprimiuRecentemente` (Map EM MEMÓRIA, por aba), que não cruzava abas. Janela de
// 15min; o guard de reload continua sendo o resyncResolvido (24h), à parte.
const reivindicarComanda = criarReivindicador("comandasImpressas", 15 * 60 * 1000, "comanda")

// Diagnóstico: só reporta ao Sentry a 1ª via duplicada evitada por sessão (ver o skip
// em processarEventoImpressao). Evita inundar o Sentry quando toda comanda iFood cai no
// dedup.
let jaReportouDedupComanda = false

// Status que NÃO aparecem no painel: os mesmos que o backend esconde na listagem
// /orders/allday (WHERE p.status not in (-5,77,99)) — FALHA_PAGAMENTO(-5),
// AGUARDANDO_PAGAMENTO(77) e CADASTRADO(99). O reconcile via SSE busca o pedido pelo
// single-order (que NÃO filtra status), então sem esta trava um pedido nesses estados
// (ex.: PIX online não pago) piscava no painel via evento leve e sumia no "Atualizar".
const STATUS_OCULTOS_DO_PAINEL = [-5, 77, 99]
const statusOcultoDoPainel = (status: number) => STATUS_OCULTOS_DO_PAINEL.includes(status)

const statusPedido = (status: number) => {
    switch (status) {
        case 0:
            return "Aguardando aceitação"

        case 1:
        case 2:
            return "Aceito"

        case 3:
        case 6:
            return "Em entrega"

        case 4:
        case -1:
        case -2:
        case -3:
        case -4:
        case -10:
            return "Finalizado"

        default:
            return "Em preparação"
    }
}

export type PedidosStoreType = {
    setState: (state: Partial<PedidosStoreType>) => void

    arrayTodosPedidos: any[]
    pedidosAguardandoAceitacao: any[]
    pedidosAceitos: any[]
    pedidosEmPreparacao: any[]
    pedidosAceitosMaisPreparacao: any[]
    pedidosEmEntrega: any[]
    pedidosFinalizados: any[]

    agrupamentos: any[]

    textoBuscaPedidos: string
    buscarPedidos: (texto: string) => void

    filtroEntregaRetirada: FiltroEntregaRetiradaEnum
    setFiltroEntregaRetirada: (filtro: FiltroEntregaRetiradaEnum) => void

    filtroMesa: string
    setFiltroMesa: (mesa: string) => void

    updatePedidosRecebidos: (response: any) => void

    redeSegurancaImpressao: () => Promise<any[]>
    processarEventoImpressao: (evento: any) => Promise<void>

    removerPedidoListaAnterior: (pedido: any) => void
    adicionarPedidoNovaLista: (pedido: any) => void
    upsertPedidoNasListas: (pedido: any) => void
    reconciliarPedido: (idPedido: any) => Promise<void>
    imprimirComandaEstacaoSePendente: (pedido: any) => void

    aceitarPedido: (pedido: any, imprimir?: boolean, automatico?: boolean) => Promise<void>
    prepararPedido: (pedido: any, imprimir?: boolean, automatico?: boolean) => Promise<void>
    despacharPedido: (pedido: any) => Promise<void>
    retirarPedido: (pedido: any) => Promise<void>
    finalizarPedido: (pedido: any) => Promise<void>
    cancelarPedido: (
        pedido: any,
        justificativa: string,
        motivoCancelamentoPedido: string
    ) => Promise<any>

    aceitarPedidosAutomaticos: (pedidos: any[]) => void
    prepararPedidosAutomaticos: (pedidos: any[]) => void

    // Rede de segurança contra eventos SSE perdidos: refaz a consulta allday, reconcilia
    // as listas (faz o pedido aparecer + aceita automaticamente os que ainda aguardam) e,
    // em modo automático com QZ, reimprime os pedidos recentes ainda pendentes nesta
    // máquina. Chamada por um intervalo global no Cabeçalho, valendo em qualquer tela.
    reimprimirPendentesRecentes: () => Promise<void>
    reofertarAceitePendentes: (pedidos: any[]) => void
    reconciliarAllday: (pedidos: any[]) => void
    redeSegurancaPedidos: () => Promise<any[] | null>

    // Fila de comandas que, SEM QZ, aguardam impressão pelo navegador acionada pelo
    // usuário (o diálogo do navegador não abre mais sozinho — a UI mostra um aviso com
    // botão "Imprimir"). Ver Cabecalho (faixa de impressão pendente).
    pedidosPendentesImpressao: any[]
    enfileirarImpressaoPendente: (pedido: any) => void
    removerImpressaoPendente: (idPedido: any) => void
}

const createPedidosStore: StateCreator<StoreType, [], [], PedidosStoreType> = (set, get) => ({
    setState: state => set(state),

    arrayTodosPedidos: [],
    pedidosAguardandoAceitacao: [],
    pedidosAceitos: [],
    pedidosEmPreparacao: [],
    pedidosAceitosMaisPreparacao: [],
    pedidosEmEntrega: [],
    pedidosFinalizados: [],

    agrupamentos: [],

    pedidosPendentesImpressao: [],
    // Enfileira sem duplicar (o mesmo pedido pode passar por mais de um caminho).
    enfileirarImpressaoPendente: pedido =>
        set(estado => {
            const fila = estado.pedidosPendentesImpressao || []
            if (fila.some((p: any) => p.id === pedido.id)) return {} as any
            return { pedidosPendentesImpressao: [...fila, pedido] }
        }),
    removerImpressaoPendente: idPedido =>
        set(estado => ({
            pedidosPendentesImpressao: (estado.pedidosPendentesImpressao || []).filter(
                (p: any) => p.id !== idPedido
            ),
        })),

    textoBuscaPedidos: "",
    buscarPedidos: (texto: string) => set({ textoBuscaPedidos: texto }),

    filtroEntregaRetirada: FiltroEntregaRetiradaEnum.TODOS,
    setFiltroEntregaRetirada: (filtro: FiltroEntregaRetiradaEnum) =>
        set({ filtroEntregaRetirada: filtro }),

    filtroMesa: "todas",
    setFiltroMesa: (mesa: string) => set({ filtroMesa: mesa }),

    updatePedidosRecebidos: response => {
        if (typeof response.data === "string") return

        set({ arrayTodosPedidos: response.data })

        const pedidosAguardandoAceitacao = [] as any[]
        const pedidosAceitos = [] as any[]
        const pedidosEmPreparacao = [] as any[]
        const pedidosEmEntrega = [] as any[]
        const pedidosFinalizados = [] as any[]

        const agrupamentos = response.data.reduce((acc: any, p: any) => {
            if (p.quantidadeRegistrosAgrupamento <= 1) return acc
            if (acc.find((a: string) => a === p.agrupamento)) return acc

            acc.push(p.agrupamento)

            return acc
        }, [])

        // Estes aceites/preparos vêm do REFRESH (rede de segurança / "Atualizar"), não de
        // um clique — por isso `automatico = true`. Sem a flag eles rodavam como se fossem
        // manuais: sem o dedup entre abas (todas as abas aceitavam o MESMO pedido, e quem
        // perdia no lock do backend ainda mostrava popup de erro) e com o Backdrop de tela
        // cheia. A rede de segurança agora roda em qualquer tela, então isso apareceria a
        // cada 1 min, inclusive por cima do Registrador. A reivindicação do aceite tem
        // janela de 60s: se o aceite falhar de verdade, o ciclo seguinte reoferece.
        response.data.forEach((pedido: any) => {
            switch (statusPedido(pedido.status)) {
                case "Aguardando aceitação":
                    pedidosAguardandoAceitacao.push(pedido)

                    if (get().configuracoes!.recebedor.prepararPedidoAutomatico) {
                        get().prepararPedido(pedido, true, true)
                        get().ativarAviso()
                    } else if (autoAceiteLigado(get().configuracoes!.recebedor)) {
                        get().aceitarPedido(pedido, true, true)
                        get().ativarAviso()
                    }

                    break
                case "Aceito":
                    pedidosAceitos.push(pedido)

                    if (get().configuracoes!.recebedor.prepararPedidoAutomatico) {
                        get().prepararPedido(pedido, true, true)
                        get().ativarAviso()
                    }

                    break
                case "Em preparação":
                    pedidosEmPreparacao.push(pedido)
                    break
                case "Em entrega":
                    pedidosEmEntrega.push(pedido)
                    break
                case "Finalizado":
                    pedidosFinalizados.push(pedido)
                    break
            }
        })

        const pedidosAceitosMaisPreparacao = pedidosAceitos
            .concat(pedidosEmPreparacao)
            .sort((a, b) => b.id - a.id)

        if (pedidosAguardandoAceitacao.length > 0) get().ativarAviso()

        set({
            pedidosAguardandoAceitacao,
            pedidosAceitos,
            pedidosEmPreparacao,
            pedidosAceitosMaisPreparacao,
            pedidosEmEntrega,
            pedidosFinalizados,
            agrupamentos,
        })
    },
    /**
     * REDE DE SEGURANÇA de impressão: busca o que ficou pendente e injeta no MESMO
     * roteador de eventos do SSE, como se tivesse chegado pelo push.
     *
     * Não dá ACK e não consome nada — o endpoint é somente leitura, então os ePDVs que
     * drenam o /eventspolling continuam recebendo os mesmos eventos. Quem impede a via
     * duplicada é o dedup por SolicitacaoImpressao.id (`reivindicarImpressao`).
     *
     * INVARIANTE: a janela do backend (10 min) é MENOR que a janela do dedup (15 min).
     * A marca de dedup é gravada quando o cliente reivindica, sempre depois do
     * dataCadastro; com janelas iguais a folga seria zero e qualquer localStorage limpo
     * no meio causaria reimpressão. Alterar uma exige alterar a outra.
     */
    redeSegurancaImpressao: async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return []
        try {
            return await impressoesPendentes(autorizacao)
        } catch (error) {
            RegistrarLogErro(error, "redeSegurancaImpressao")
            return []
        }
    },
    // Processa UM evento de impressão (PRINT/PRINT{n}, PLACED, CUPOM_FISCAL), venha ele
    // do push SSE (kind="IMPRESSAO") ou da rede de segurança — o payload é o mesmo, só
    // muda o transporte. Dedup por SolicitacaoImpressao.id garante que os dois caminhos
    // não dupliquem e que cada navegador imprima 1x (multi-estação).
    //
    // NÃO existe ACK aqui, por desenho: o ACK consome a fila do servidor e roubaria o
    // evento das integrações que leem por polling.
    processarEventoImpressao: async evento => {
        if (!evento) return

        const configuracoes = get().configuracoes
        const autorizacao = localStorage.getItem("chaveSistema")
        const code: string = evento.code || evento.tipo || ""

        // Dedup por SolicitacaoImpressao.id — SÓ para IMPRESSÃO pura (PRINT/CUPOM):
        // garante 1 impressão por navegador (compartilhado entre abas via localStorage)
        // e evita reimprimir o que veio por push e por drain. NÃO se aplica ao PLACED:
        // a EXIBIÇÃO da lista precisa acontecer em toda tela (o aceite/impressão do
        // PLACED já é dedup por `ultimoImprimir`/status).
        // Retorna true quando ESTA aba deve PULAR a impressão (id nulo, já impresso
        // por outra aba/estação, ou perdeu a corrida do check-and-set atômico).
        // A chave é o SolicitacaoImpressao.id: ÚNICO identificador que dedupica a MESMA
        // solicitação entre abas/conexões E deixa uma REIMPRESSÃO (novo id) sair. Exige
        // que o backend inclua `id` no push do SSE, igual em toda conexão e distinto por
        // solicitação. Sem `id`, não há como distinguir reimpressão de entrega duplicada.
        const dedupImpressao = async () => {
            if (evento.id == null) return false
            return !(await reivindicarImpressao(evento.id))
        }

        // Modo passivo (Visualização/Garçom): apenas acompanha (ou nem isso), nunca imprime
        // as comandas que chegam, independente da configuração de impressão (só o PLACED, que
        // atualiza a lista, segue).
        const emModoPassivo = ehModoPassivo(get().tabStatus)

        try {
            // PRINT / PRINT{n}: imprime direto o texto da comanda.
            if (code.match(/PRINT(\d?)/)) {
                if (emModoPassivo) return
                if (await dedupImpressao()) return
                if (
                    configuracoes!.printer.imprimirComandasIfood === false &&
                    evento.plataforma === "ifood_integracao"
                )
                    return
                if (
                    configuracoes!.printer.imprimir99automatico === false &&
                    !!evento.didiOrderUUID
                )
                    return

                const numeroDaImpressora = code.match(/PRINT(\d)/)
                    ? Number(code.replace(/PRINT(\d)/, "$1"))
                    : 0
                const orderIdComanda = evento.correlationId ?? evento.pedidoId
                // MESA (tipoPedido "2") reusa o MESMO pedido.id em TODA rodada de itens —
                // cada adição gera uma comanda nova e legítima. Por isso a mesa NÃO pode
                // passar pelo dedup por pedido.id (senão, da 2ª rodada em diante, a comanda
                // do item adicionado seria bloqueada). O bridge vale só para não-mesa
                // (delivery/retirada), que é onde o iFood chega já aceito e duplicava.
                const ehMesa = evento.type === "2"

                // Ponte entre os DOIS baldes de dedup da comanda COMPLETA (PRINT, sem
                // número). O aceite/preparo/estação deduplica a comanda por `pedido.id`
                // (reivindicarComanda); o push deduplicava SÓ por SolicitacaoImpressao.id
                // (reivindicarImpressao, acima). Um pedido iFood chega JÁ ACEITO → sai
                // pela via de estação (chave pedido.id) E ainda chegava pelo push (chave
                // SolicitacaoImpressao.id) → 2 vias, porque os baldes não se cruzavam.
                // Passando o push da comanda completa pelo MESMO balde atômico por pedido,
                // só a 1ª via sai — não importa qual caminho chegou primeiro.
                // NÃO se aplica a PRINT1..n (impressão ALTERNADA por setor) nem a MESA:
                // ambas têm vias legítimas repetidas; seguem só com o dedup por evento.id.
                // Reimpressão MANUAL não passa por aqui (chama mandarImprimir direto).
                if (
                    !ehMesa &&
                    numeroDaImpressora === 0 &&
                    orderIdComanda != null &&
                    !(await reivindicarComanda(orderIdComanda))
                ) {
                    console.log(
                        `[IMPRESSAO] comanda ${code} PULADA — pedido ${orderIdComanda} já saiu nesta máquina (aceite/estação/push)`
                    )
                    // Diagnóstico durável: registra no Sentry a 1ª via duplicada evitada
                    // NESTA sessão (uma vez só, para não inundar o Sentry — lojas afetadas
                    // duplicavam todo pedido iFood). Confirma em produção que o dedup entre
                    // caminhos está pegando. Nunca deixa a impressão quebrar por causa do log.
                    if (!jaReportouDedupComanda) {
                        jaReportouDedupComanda = true
                        try {
                            Sentry.captureMessage(
                                "Comanda duplicada evitada (dedup push × aceite)",
                                {
                                    level: "info",
                                    tags: { area: "impressao", motivo: "dedup-comanda-completa" },
                                    extra: {
                                        pedido: orderIdComanda,
                                        code,
                                        plataforma: evento.plataforma ?? null,
                                        eventoId: evento.id ?? null,
                                    },
                                }
                            )
                        } catch {
                            /* best-effort */
                        }
                    }
                    return
                }

                console.log(
                    `[IMPRESSAO] comanda ${code} — pedido ${
                        orderIdComanda ?? "?"
                    }, evento.id ${evento.id ?? "(sem id)"}`
                )
                get().mandarImprimir({
                    pedido: evento,
                    texto: evento.texto,
                    qtd: 1,
                    qrcode: verificarPrintQRCode(evento),
                    QZObrigatorio: true,
                    numeroDaImpressora,
                })
                if (!ehMesa && numeroDaImpressora === 0 && orderIdComanda != null)
                    marcarResyncResolvido(orderIdComanda)
                return
            }

            switch (code) {
                case "PLACED": {
                    const idPedido = evento.correlationId || evento.pedidoId
                    const retorno = await buscarInformacoesPedido(autorizacao, idPedido)
                    if (!retorno?.id || retorno.status < 0) return

                    // EXIBE em TODA tela: reconcilia o pedido na lista certa pelo status
                    // atual, independente de quem aceitou (corrige multi-estação).
                    get().upsertPedidoNasListas(retorno)

                    // Aceite/impressão automático só se ainda aguardando (idempotente;
                    // a perdedora da corrida já exibiu acima e será reconciliada pelo STATUS).
                    if (retorno.status === 0) {
                        // Som de novo pedido (fallback): a fonte principal do toque é o
                        // evento LEVE `{tipo:"PLACED"}` do SSE (handler global do
                        // Cabeçalho). Quando esse evento leve não chega, o pedido só
                        // "avisava" no refresh periódico (~1 min). Tocamos aqui também,
                        // no evento PESADO de novo pedido ainda aguardando — caminho que
                        // roda sempre no Recebedor aberto e no aceite automático. O áudio
                        // é em loop e `ativarAviso` é idempotente, então um eventual toque
                        // duplo com o evento leve é inofensivo.
                        get().ativarAviso()

                        // Chamada em modo AUTOMÁTICO: o dedup por navegador (só UMA aba
                        // aceita/imprime) acontece DENTRO de aceitarPedido/prepararPedido
                        // (reivindicarAceite + reivindicarComanda), cobrindo também as
                        // outras rotas de aceite (aceitarPedidosAutomaticos no refresh).
                        if (configuracoes!.recebedor.prepararPedidoAutomatico)
                            get().prepararPedido(retorno, true, true)
                        else if (autoAceiteLigado(configuracoes!.recebedor))
                            get().aceitarPedido(retorno, true, true)
                    } else if (
                        // Estação que "perdeu" a corrida do aceite (com aceite/preparo
                        // automático): o pedido já foi avançado por OUTRA estação, então
                        // não entra no aceite acima. Ainda assim, garante a via DESTA
                        // estação — o backend transmite a comanda a todas, quem imprime é
                        // decisão do frontend. Dedup por estação evita duplicar.
                        (autoAceiteLigado(configuracoes!.recebedor) ||
                            configuracoes!.recebedor.prepararPedidoAutomatico) &&
                        [1, 2, 7].includes(retorno.status)
                    ) {
                        get().imprimirComandaEstacaoSePendente(retorno)
                    }
                    break
                }
                case "CUPOM_FISCAL":
                    if (emModoPassivo) return
                    if (await dedupImpressao()) return
                    console.log(
                        `[IMPRESSAO] cupom fiscal — pedido ${evento.correlationId ?? "?"}, evento.id ${
                            evento.id ?? "(sem id)"
                        }`
                    )
                    if (evento.texto?.includes("Cupom Cancelado"))
                        imprimirNotaGeral({
                            texto: evento.texto + "\n\n\n\n\n",
                            printer: configuracoes!.printer,
                            impressora: configuracoes!.printer.impressoras[0],
                        })
                    else imprimirNotaFiscal(evento.correlationId)
                    break
                case "UPDATE":
                default:
                    break
            }
        } catch (error) {
            console.error(error)
            RegistrarLogErro(error, "processarEventoImpressao", evento)
        }
    },
    removerPedidoListaAnterior: pedido => {
        try {
            switch (statusPedido(pedido.status)) {
                case "Aguardando aceitação":
                    set(prev => ({
                        pedidosAguardandoAceitacao: prev.pedidosAguardandoAceitacao.filter(
                            p => p.id !== pedido.id
                        ),
                    }))
                    break
                case "Aceito":
                    set(prev => ({
                        pedidosAceitos: prev.pedidosAceitos.filter(p => p.id !== pedido.id),
                        pedidosAceitosMaisPreparacao: prev.pedidosAceitosMaisPreparacao.filter(
                            p => p.id !== pedido.id
                        ),
                    }))
                    break
                case "Em preparação":
                    set(prev => ({
                        pedidosEmPreparacao: prev.pedidosEmPreparacao.filter(
                            p => p.id !== pedido.id
                        ),
                        pedidosAceitosMaisPreparacao: prev.pedidosAceitosMaisPreparacao.filter(
                            p => p.id !== pedido.id
                        ),
                    }))
                    break
                case "Em entrega":
                    set(prev => ({
                        pedidosEmEntrega: prev.pedidosEmEntrega.filter(p => p.id !== pedido.id),
                    }))
                    break
                case "Finalizado":
                    set(prev => ({
                        pedidosFinalizados: prev.pedidosFinalizados.filter(p => p.id !== pedido.id),
                    }))
                    break
            }
        } catch (error) {
            console.error(error)
            RegistrarLogErro(error, "removerPedidoListaAnterior", pedido)
        }
    },
    adicionarPedidoNovaLista: async pedido => {
        // Protege contra respostas invalidas (ex.: AceitarPedido/PrepararPedido que
        // retornam undefined quando a requisicao falha) para nao quebrar a tela.
        if (!pedido?.id) return
        // Não insere pedido em status oculto (77/99/-5) — alinha com upsertPedidoNasListas
        // e com o /orders/allday, evitando card fantasma "Não Identificado" no painel.
        if (statusOcultoDoPainel(pedido.status)) return
        switch (statusPedido(pedido.status)) {
            case "Aceito":
                set(prev => ({
                    pedidosAceitos: [pedido, ...prev.pedidosAceitos],
                    pedidosAceitosMaisPreparacao: [pedido, ...prev.pedidosAceitosMaisPreparacao],
                }))
                break

            case "Em preparação":
                // TODO: Checar se precisa
                pedido.status = 7

                set(prev => ({
                    pedidosEmPreparacao: [pedido, ...prev.pedidosEmPreparacao],
                    pedidosAceitosMaisPreparacao: [pedido, ...prev.pedidosAceitosMaisPreparacao],
                }))
                break

            case "Em entrega":
                set(prev => ({ pedidosEmEntrega: [pedido, ...prev.pedidosEmEntrega] }))

                break

            case "Finalizado":
                set(prev => ({ pedidosFinalizados: [pedido, ...prev.pedidosFinalizados] }))

                break
        }
    },
    // Insere/atualiza o pedido na lista certa pelo status ATUAL, removendo-o de
    // qualquer outra lista. Idempotente (por id). É o que garante que toda estação
    // exiba o pedido, independente de qual delas aceitou/mudou o status.
    upsertPedidoNasListas: pedido => {
        if (!pedido?.id) return
        set(prev => {
            const sem = (arr: any[]) => arr.filter(p => p.id !== pedido.id)
            const listas = {
                pedidosAguardandoAceitacao: sem(prev.pedidosAguardandoAceitacao),
                pedidosAceitos: sem(prev.pedidosAceitos),
                pedidosEmPreparacao: sem(prev.pedidosEmPreparacao),
                pedidosAceitosMaisPreparacao: sem(prev.pedidosAceitosMaisPreparacao),
                pedidosEmEntrega: sem(prev.pedidosEmEntrega),
                pedidosFinalizados: sem(prev.pedidosFinalizados),
            }
            // Pedido em status oculto (ex.: PIX online não pago = 77): garante que ele saia
            // das listas e NÃO seja re-inserido. Alinha o tempo real com o /orders/allday.
            if (statusOcultoDoPainel(pedido.status)) {
                return listas
            }
            switch (statusPedido(pedido.status)) {
                case "Aguardando aceitação":
                    listas.pedidosAguardandoAceitacao = [pedido, ...listas.pedidosAguardandoAceitacao]
                    break
                case "Aceito":
                    listas.pedidosAceitos = [pedido, ...listas.pedidosAceitos]
                    listas.pedidosAceitosMaisPreparacao = [
                        pedido,
                        ...listas.pedidosAceitosMaisPreparacao,
                    ]
                    break
                case "Em preparação":
                    listas.pedidosEmPreparacao = [pedido, ...listas.pedidosEmPreparacao]
                    listas.pedidosAceitosMaisPreparacao = [
                        pedido,
                        ...listas.pedidosAceitosMaisPreparacao,
                    ]
                    break
                case "Em entrega":
                    listas.pedidosEmEntrega = [pedido, ...listas.pedidosEmEntrega]
                    break
                case "Finalizado":
                    listas.pedidosFinalizados = [pedido, ...listas.pedidosFinalizados]
                    break
            }
            return listas
        })
    },
    // Busca o pedido e reconcilia na lista certa (usado ao receber um evento leve
    // de STATUS: mantém todas as estações em dia mesmo quem não fez a ação).
    reconciliarPedido: async idPedido => {
        if (!idPedido) return
        try {
            const autorizacao = localStorage.getItem("chaveSistema")
            const pedido = await buscarInformacoesPedido(autorizacao, idPedido)
            if (pedido?.id) get().upsertPedidoNasListas(pedido)
        } catch (error) {
            RegistrarLogErro(error, "reconciliarPedido", idPedido)
        }
    },
    // Imprime a comanda DESTA estação, uma única vez, sem reaceitar. Usado na corrida
    // multi-estação com aceite automático: a estação que "perde" a corrida vê o pedido
    // já avançado (não entra no aceite) mas ainda precisa imprimir a sua via. Mesmas
    // regras do caminho de aceite; dedup por pedido (localStorage, por navegador).
    imprimirComandaEstacaoSePendente: async pedido => {
        try {
            if (!pedido?.id) return
            if (ehModoPassivo(get().tabStatus)) return
            const cfg = get().configuracoes
            if (!cfg) return
            if (get().tipoPainelImpressao() !== "plugin") {
                if ((cfg.printer.impressoras?.length ?? 0) > 0) avisarQzDesconectado()
                return
            }
            if (pedido.imprimir === false) return
            if (
                cfg.printer.imprimirComandasIfood === false &&
                pedido.plataforma === "ifood_integracao"
            )
                return
            if (cfg.printer.imprimir99automatico === false && !!pedido.didiOrderUUID) return
            if (resyncJaResolvido(pedido.id)) return
            if (get().imprimiuRecentemente(pedido.id)) return
            // Dedup atômico por navegador: se a comanda deste pedido já saiu em OUTRA aba
            // (aceite, preparo ou outra via de estação), pula — evita 2 vias no mesmo PC.
            if (!(await reivindicarComanda(pedido.id))) return

            get().mandarImprimir({
                pedido,
                texto: pedido.texto,
                qtd: cfg.user!.parametros.viasImpressao,
                qrcode: verificarPrintQRCode(pedido),
            })
            marcarResyncResolvido(pedido.id)
        } catch (error) {
            RegistrarLogErro(error, "imprimirComandaEstacaoSePendente", pedido)
        }
    },
    aceitarPedido: async (pedido, imprimir = true, automatico = false) => {
        if (ehModoPassivo(get().tabStatus)) {
            return
        }

        // Aceite AUTOMÁTICO (PLACED do SSE, refresh via aceitarPedidosAutomaticos): dedup
        // por navegador — só a 1ª aba/handler a reivindicar aceita; as demais pulam. Evita
        // aceites concorrentes (e o popup de erro do perdedor). O aceite MANUAL (operador
        // clicou) NÃO passa por aqui: sempre procede e mostra erro se falhar.
        if (automatico) {
            const venceu = await reivindicarAceite(pedido.id)
            console.log(
                `[ACEITE] pedido ${pedido.id} — ${
                    venceu ? "reivindicado (esta aba aceita/imprime)" : "já tratado (pulado)"
                }`
            )
            // Perder a corrida vale só para a AÇÃO (aceitar + imprimir), que é da aba
            // vencedora. A LISTA desta aba precisa acompanhar do mesmo jeito: antes o
            // `return` seco deixava o pedido parado na coluna de aguardando (ou fora da
            // tela) até a rede de segurança de 1 min. Ficou visível quando o Registrador
            // passou a processar os eventos: com ele aberto, era ele quem vencia a corrida
            // e o Painel de Pedidos não mostrava o pedido novo.
            if (!venceu) {
                if (!(await reconciliarAcaoConcorrente(get, pedido, imprimir, true)))
                    await get().reconciliarPedido(pedido.id)
                return
            }
        }

        // Spinner só no aceite MANUAL: ali o operador clicou e está esperando. O aceite
        // AUTOMÁTICO roda em segundo plano, em qualquer tela — e o `Loading` do store é um
        // Backdrop de tela cheia com pointerEvents: "all", montado em ~20 telas. Cobrir o
        // Registrador no meio do lançamento de um pedido (ou o próprio painel a cada
        // pedido que entra) engole clique e digitação sem o operador ter pedido nada.
        if (!automatico) useLoadingStore.getState().setLoading(true)

        try {
            const response = await AceitarPedido(pedido.id)

            // Sem pedido no retorno: pode ser falha real (rede/servidor → undefined) OU
            // a corrida multi-estação em que OUTRA estação aceitou primeiro e o backend
            // devolveu sucesso com body vazio (lock otimista). Reconcilia: se já está
            // aceito, esta estação só imprime a sua via, sem erro. No modo AUTOMÁTICO não
            // mostra popup (o operador não clicou nada; a rede de segurança reoferece).
            if (!response?.id) {
                if (await reconciliarAcaoConcorrente(get, pedido, imprimir, true)) return
                if (!automatico)
                    alertStart("Não foi possível aceitar o pedido. Tente novamente.", "error")
                // Falha REAL (não foi corrida vencida por outra estação): libera a marca
                // otimista para o PRÓXIMO ciclo do resync reofertar já — senão a marca de
                // 60s do reivindicarAceite trava a re-oferta e o backend expira o pedido
                // (-4). Mesmo tratamento do prepararPedido.
                if (automatico) reivindicarAceite.liberar(pedido.id)
                return
            }

            // upsert (remove de todas as listas por id e reinsere na certa pelo status
            // ATUAL) em vez de removerAnterior+adicionarNova: este último usa um switch que
            // NÃO trata "Aguardando aceitação" (status 0), entao se o AceitarPedido responde
            // com o pedido ainda em status 0 (o commit propaga um instante depois), o card
            // era removido de "Aguardando" e nao reinserido -> sumia do painel ate o resync.
            get().upsertPedidoNasListas(response)
            // Avisa as OUTRAS abas deste navegador que o pedido mudou de status, para elas
            // reconciliarem a exibição na hora (sem esperar o allday de ~1 min). Cobre o
            // aceite MANUAL também, que não emite evento SSE — antes o Recebedor de outra aba
            // seguia mostrando o pedido em "Aguardando" até o próximo allday.
            anunciarPedidoAtualizado(response)

            if (imprimir) {
                // QZ pode ter caído: tenta reconectar antes de decidir imprimir — mas SEM
                // travar o aceite (o pedido já entrou na lista acima). Com o QZ fechado, o
                // connect cicla portas por muitos segundos; religarQzSeAplicavel pula/limita
                // essa espera para o spinner não ficar preso.
                if (!get().qzAtivo()) {
                    await get().religarQzSeAplicavel()
                }

                const usarQz = get().qzAtivo() && get().tipoPainelImpressao() === "plugin"

                // SEM QZ o ACEITE não imprime. A impressão pelo NAVEGADOR (window.print)
                // é disparada uma única vez no estágio de PREPARO (ver prepararPedido) —
                // manual ou automático. Isso evita o diálogo de impressão abrir no aceite
                // e de novo no preparar. Só o caminho QZ imprime aqui.
                if (usarQz) {
                    if (
                        localStorage.getItem("ultimoImprimir") !== pedido.id &&
                        pedido.imprimir !== false
                    ) {
                        localStorage.setItem("ultimoImprimir", pedido.id)

                        if (
                            get().configuracoes!.printer.imprimirComandasIfood === false &&
                            pedido.plataforma === "ifood_integracao"
                        )
                            return

                        if (
                            get().configuracoes!.printer.imprimir99automatico === false &&
                            !!pedido.didiOrderUUID
                        )
                            return

                        // Respeita a preferência de não auto-imprimir quando configurado
                        // como navegador.
                        if (
                            get().configuracoes?.printer.tipoImpressao === "navegador" &&
                            !get().configuracoes?.recebedor.imprimirPedidoAutomatico
                        )
                            return

                        // Gate atômico por navegador: só imprime se a comanda deste pedido
                        // ainda não saiu em outra aba/rota (dedup real entre abas).
                        if (
                            !get().imprimiuRecentemente(pedido.id) &&
                            (await reivindicarComanda(pedido.id))
                        ) {
                            get().mandarImprimir({
                                pedido: pedido,
                                texto: pedido.texto,
                                qtd: get().configuracoes!.user!.parametros.viasImpressao,
                                qrcode: verificarPrintQRCode(pedido),
                            })
                            marcarResyncResolvido(pedido.id)
                        }
                    }
                } else if (
                    // Aceite MANUAL esperando QZ, mas ele está offline: avisa. No modo
                    // automático não avisa — a comanda sai no preparo (navegador).
                    !autoAceiteLigado(get().configuracoes!.recebedor) &&
                    !get().configuracoes!.recebedor.prepararPedidoAutomatico &&
                    (get().configuracoes!.printer.impressoras?.length ?? 0) > 0
                ) {
                    avisarQzDesconectado()
                }
            }
        } catch (error) {
            // Falhou por exceção (rede/servidor): libera a marca otimista do aceite para
            // o resync reofertar no próximo ciclo, em vez de segurar os 60s e deixar o
            // pedido expirar.
            if (automatico) reivindicarAceite.liberar(pedido.id)
            RegistrarLogErro(error, "aceitarPedido", { pedido: pedido } as unknown as string)
        } finally {
            if (!automatico) useLoadingStore.getState().setLoading(false)
        }
    },
    prepararPedido: async (pedido, imprimir = true, automatico = false) => {
        if (ehModoPassivo(get().tabStatus)) {
            return
        }

        // Preparo AUTOMÁTICO: mesmo dedup por navegador do aceite (só a 1ª aba prepara).
        if (automatico) {
            const venceu = await reivindicarPreparo(pedido.id)
            console.log(
                `[PREPARO] pedido ${pedido.id} — ${
                    venceu ? "reivindicado (esta aba prepara/imprime)" : "já tratado (pulado)"
                }`
            )
            // Mesma regra do aceite: quem perde a corrida não prepara nem imprime, mas
            // reconcilia a lista para não ficar exibindo o pedido no estágio anterior.
            if (!venceu) {
                const podeImprimir = !!get().configuracoes?.recebedor.prepararPedidoAutomatico
                if (!(await reconciliarAcaoConcorrente(get, pedido, imprimir, podeImprimir)))
                    await get().reconciliarPedido(pedido.id)
                return
            }
        }

        // Mesma regra do aceite: sem Backdrop global no preparo automático.
        if (!automatico) useLoadingStore.getState().setLoading(true)

        try {
            const response = await PrepararPedido(pedido.id)

            // Igual ao aceite: body vazio pode ser a corrida multi-estação (lock otimista).
            // Reconcilia e, em loja de preparo automático, imprime a via desta estação. No
            // modo automático não mostra popup (a rede de segurança reoferece).
            if (!response?.id) {
                const podeImprimir = !!get().configuracoes?.recebedor.prepararPedidoAutomatico
                if (await reconciliarAcaoConcorrente(get, pedido, imprimir, podeImprimir)) return
                if (!automatico)
                    alertStart("Não foi possível preparar o pedido. Tente novamente.", "error")
                // Falha real (não foi corrida vencida por outra estação): libera a marca
                // para o próximo ciclo do resync tentar de novo, em vez de segurar os 60s.
                if (automatico) reivindicarPreparo.liberar(pedido.id)
                return
            }

            get().removerPedidoListaAnterior(pedido)
            get().adicionarPedidoNovaLista(response)
            // Sincroniza as outras abas na hora (ver aceitarPedido).
            anunciarPedidoAtualizado(response)

            // Um pedido pode precisar de DOIS passos até "Em preparação" (aceitar e depois
            // preparar). O PrepararPedido às vezes avança só um (ex.: Aguardando -> Aceito) e
            // devolve o pedido AINDA fora de "Em preparação". Como a reivindicação é otimista
            // (marca antes de agir) e dura 60s, sem liberar aqui o próximo ciclo do
            // resync/allday cairia em "já tratado (pulado)" e o pedido ficaria preso em
            // "Aceito" até a marca expirar. Liberamos para o ciclo seguinte concluir a
            // transição na hora. Ao chegar em "Em preparação" a marca é mantida, então não há
            // preparo nem impressão em dobro.
            if (automatico && statusPedido(response.status) !== "Em preparação") {
                reivindicarPreparo.liberar(pedido.id)
            }

            if (imprimir) {
                // QZ pode ter caído: tenta reconectar antes de decidir imprimir — mas SEM
                // travar o preparo (o pedido já entrou na lista acima). religarQzSeAplicavel
                // pula/limita a espera para o spinner não ficar preso com o QZ fechado.
                if (get().tipoPainelImpressao() !== "plugin") {
                    await get().religarQzSeAplicavel()
                }

                const usarQz = get().tipoPainelImpressao() === "plugin"
                const isElectron =
                    typeof navigator !== "undefined" &&
                    navigator.userAgent.includes("Electron")
                // Modo automático (aceite e/ou preparo). Fora dele (operação 100% manual)
                // não há impressão automática pelo navegador — o operador usa o botão de
                // imprimir.
                const modoAutomatico =
                    autoAceiteLigado(get().configuracoes!.recebedor) ||
                    !!get().configuracoes!.recebedor.prepararPedidoAutomatico
                // SEM QZ e fora do app desktop: o PREPARO é o ÚNICO ponto de impressão
                // pelo NAVEGADOR (window.print). O aceite não imprime mais sem QZ, então
                // sai exatamente 1 diálogo de impressão por pedido, aqui no "preparar"
                // (manual ou automático).
                const fallbackNavegador = !usarQz && !isElectron && modoAutomatico

                if (
                    (usarQz || fallbackNavegador) &&
                    localStorage.getItem("ultimoImprimir") !== pedido.id &&
                    pedido.imprimir !== false
                ) {
                    localStorage.setItem("ultimoImprimir", pedido.id)

                    if (
                        get().configuracoes!.printer.imprimirComandasIfood === false &&
                        pedido.plataforma === "ifood_integracao"
                    )
                        return

                    if (
                        get().configuracoes!.printer.imprimir99automatico === false &&
                        !!pedido.didiOrderUUID
                    )
                        return

                    // No caminho QZ mantém a regra atual: só imprime no preparo quando o
                    // preparo automático está ligado (no aceite-automático a via já saiu no
                    // aceite). No fallback do navegador o objetivo é imprimir aqui, então
                    // não exige o preparo automático.
                    if (usarQz && !get().configuracoes?.recebedor.prepararPedidoAutomatico)
                        return

                    if (
                        !get().imprimiuRecentemente(pedido.id) &&
                        (await reivindicarComanda(pedido.id))
                    ) {
                        if (fallbackNavegador) {
                            // SEM QZ: NÃO abre o diálogo do navegador sozinho (ele é
                            // modal/disruptivo e, com drivers problemáticos, trava a
                            // pré-visualização e a tela). Em vez disso, enfileira a comanda
                            // e a UI mostra um aviso com botão "Imprimir" para o usuário
                            // acionar quando quiser (ver Cabecalho).
                            get().enfileirarImpressaoPendente(pedido)
                        } else {
                            get().mandarImprimir({
                                pedido: pedido,
                                texto: pedido.texto,
                                qtd: get().configuracoes!.user!.parametros.viasImpressao,
                                qrcode: verificarPrintQRCode(pedido),
                                forcarNavegador: false,
                            })
                        }
                        // Marca "impresso nesta máquina" (persistente, 24h) → dedup por
                        // estação: impede reimpressão pela reconexão e pela via
                        // multi-estação (imprimirComandaEstacaoSePendente).
                        marcarResyncResolvido(pedido.id)
                    }
                } else if (
                    !usarQz &&
                    !fallbackNavegador &&
                    (get().configuracoes!.printer.impressoras?.length ?? 0) > 0
                ) {
                    // Espera QZ mas está offline e não caiu no fallback (modo manual/app
                    // desktop): avisa.
                    avisarQzDesconectado()
                }
            }
        } catch (error) {
            RegistrarLogErro(error, "prepararPedido", { pedido: pedido } as unknown as string)
        } finally {
            if (!automatico) useLoadingStore.getState().setLoading(false)
        }
    },
    despacharPedido: async pedido => {
        useLoadingStore.getState().setLoading(true)

        try {
            const response = await DespacharPedido(pedido.id)

            get().removerPedidoListaAnterior(pedido)
            get().adicionarPedidoNovaLista(response)
            anunciarPedidoAtualizado(response)
        } catch (error) {
            RegistrarLogErro(error, "despacharPedido", { pedido: pedido } as unknown as string)
        } finally {
            useLoadingStore.getState().setLoading(false)
        }
    },
    // TODO: Apagar e usar "despacharPedido"?
    retirarPedido: async pedido => {
        useLoadingStore.getState().setLoading(true)

        try {
            const response = await DespacharPedido(pedido.id)

            get().removerPedidoListaAnterior(pedido)
            get().adicionarPedidoNovaLista(response)
            anunciarPedidoAtualizado(response)
        } catch (error) {
            RegistrarLogErro(error, "prontoRetirada", { pedido: pedido } as unknown as string)
        } finally {
            useLoadingStore.getState().setLoading(false)
        }
    },
    finalizarPedido: async pedido => {
        useLoadingStore.getState().setLoading(true)

        try {
            const response = await FinalizarPedido(pedido.id)

            get().removerPedidoListaAnterior(pedido)
            get().adicionarPedidoNovaLista(response)
            anunciarPedidoAtualizado(response)
        } catch (error) {
            RegistrarLogErro(error, "finalizarPedido", { pedido: pedido } as unknown as string)
        } finally {
            useLoadingStore.getState().setLoading(false)
        }
    },
    cancelarPedido: async (pedido, observacao, motivoCancelamentoPedido) => {
        // if (get().tabStatus === TabStatusEnum.VIEW) {
        // console.log("cancelarPedido não realizado por conta de tabStatus VIEW")
        // return
        // }

        useLoadingStore.getState().setLoading(true)

        const data = {
            observacao,
            motivoCancelamentoPedido,
        }

        let response = undefined

        try {
            response = await CancelarPedido(pedido.id, data)

            get().removerPedidoListaAnterior(pedido)
            get().adicionarPedidoNovaLista(response)
            anunciarPedidoAtualizado(response)
        } catch (error) {
            RegistrarLogErro(error, "cancelarPedido", { pedido: pedido } as unknown as string)
        } finally {
            useLoadingStore.getState().setLoading(false)

            return response
        }
    },
    aceitarPedidosAutomaticos: async (arrayPedidos: any[]) => {
        if (ehModoPassivo(get().tabStatus)) {
            return
        }

        if (!arrayPedidos.length) return

        try {
            for (let i = 0; i < arrayPedidos.length; i++) {
                const pedido = arrayPedidos[i]

                if (pedido.status === 0) {
                    get().aceitarPedido(pedido, true, true)
                }
            }
        } catch (error) {
            RegistrarLogErro(error, "aceitarPedidosAutomaticos", arrayPedidos as unknown as string)
        }
    },
    prepararPedidosAutomaticos: async (arrayPedidos: any[]) => {
        if (ehModoPassivo(get().tabStatus)) {
            return
        }

        if (!arrayPedidos.length) return

        try {
            for (let i = 0; i < arrayPedidos.length; i++) {
                const pedido = arrayPedidos[i]
                if (pedido.status >= 0 && pedido.status <= 2) {
                    get().prepararPedido(pedido, true, true)
                }
            }
        } catch (error) {
            RegistrarLogErro(error, "prepararPedidosAutomaticos", arrayPedidos as unknown as string)
        }
    },

    // Rede de segurança do ACEITE: reoferece o aceite/preparo automático dos pedidos que
    // seguem em "Aguardando aceitação" (status 0) no allday, para pedidos que o push ao
    // vivo exibiu mas não aceitou (evento pesado perdido, ou aceite que falhou/foi pulado).
    // Chamada pela redeSegurancaPedidos quando a assinatura NÃO mudou — cenário em que o
    // gate de redesenho pularia o único re-aceite e o backend acabava expirando o pedido.
    // Roda em QUALQUER tela (o intervalo é global no Cabeçalho) e NÃO depende do QZ nem da
    // tela do painel estar aberta. É idempotente: reivindicarAceite/reivindicarComanda
    // dedupam entre abas/estações, e aceitarPedido/prepararPedido já ignoram quando não
    // está em modo automático ou em Visualização (VIEW).
    reofertarAceitePendentes: (pedidos: any[]) => {
        try {
            if (get().tabStatus !== TabStatusEnum.CRUD) return
            const cfg = get().configuracoes
            if (!cfg) return
            const preparoAuto = !!cfg.recebedor.prepararPedidoAutomatico
            if (!preparoAuto && !autoAceiteLigado(cfg.recebedor)) return
            ;(pedidos || []).forEach((pedido: any) => {
                if (statusPedido(pedido.status) !== "Aguardando aceitação") return
                if (preparoAuto) get().prepararPedido(pedido, true, true)
                else get().aceitarPedido(pedido, true, true)
            })
        } catch (error) {
            RegistrarLogErro(error, "reofertarAceitePendentes")
        }
    },

    // SÓ EM MODO AUTOMÁTICO (aceite ou preparo) e com QZ (plugin): reimprime os pedidos
    // RECENTES (últimos JANELA_REIMPRESSAO_MIN min) em situação 0, 1 ou 2 que ainda NÃO
    // foram impressos nesta máquina. Cada impressão marca o pedido como resolvido
    // (localStorage, 24h) para não repetir no ciclo seguinte. Em modo MANUAL não imprime:
    // quem imprime é o aceite do operador.
    //
    // Passa por `reivindicarComanda(pedido.id)` — a MESMA trava atômica (Web Lock) que o
    // push/rede-de-segurança da impressão usa para a comanda completa — antes de mandar
    // imprimir. Sem isso, este "varredura às cegas" (baseada só no `resyncJaResolvido`,
    // marcado DEPOIS do mandarImprimir) podia correr no MESMO ciclo de 1min em que o push
    // do pedido ainda estava sendo impresso, e as duas vias saíam: a comanda inteira
    // duplicava. Quem reivindica primeiro (o push/catch-up ou este fallback) imprime;
    // o outro é pulado. Mesa não precisa de tratamento especial aqui: ela nunca reivindica
    // esta trava pelo push (ver `ehMesa` em processarEventoImpressao), então esta função
    // continua livre para imprimir a comanda inicial de um pedido de mesa normalmente.
    reimprimirPendentesRecentes: async () => {
        try {
            const cfg = get().configuracoes
            if (!cfg) return
            if (get().tabStatus !== TabStatusEnum.CRUD) return
            if (get().tipoPainelImpressao() !== "plugin") return
            if (!autoAceiteLigado(cfg.recebedor) && !cfg.recebedor.prepararPedidoAutomatico) return

            const agora = Date.now()
            const candidatos = [
                ...(get().pedidosAguardandoAceitacao || []),
                ...(get().pedidosAceitos || []),
            ].filter((p: any) => {
                if (![0, 1, 2].includes(p.status)) return false
                if (resyncJaResolvido(p.id)) return false
                if (p.imprimir === false) return false
                if (
                    cfg.printer.imprimirComandasIfood === false &&
                    p.plataforma === "ifood_integracao"
                )
                    return false
                if (cfg.printer.imprimir99automatico === false && p.didiOrderUUID) return false
                const minutos = (agora - new Date(p.createdAt).getTime()) / 60000
                if (!(minutos >= 0 && minutos <= JANELA_REIMPRESSAO_MIN)) return false
                return true
            })

            for (const pedido of candidatos) {
                if (!(await reivindicarComanda(pedido.id))) {
                    // Já saiu (ou está saindo agora) pelo push/catch-up — não duplica, só
                    // marca resolvido para não reavaliar este pedido nos próximos ciclos.
                    marcarResyncResolvido(pedido.id)
                    continue
                }
                get().mandarImprimir({
                    pedido,
                    texto: pedido.texto,
                    qtd: cfg.user?.parametros?.viasImpressao,
                })
                marcarResyncResolvido(pedido.id)
            }
        } catch (error) {
            RegistrarLogErro(error, "reimprimirPendentesRecentes")
        }
    },

    // Reconcilia as listas a partir de um snapshot do allday (o array de pedidos), venha
    // ele do fetch desta aba (líder) ou repassado por outra aba via BroadcastChannel. NÃO
    // faz rede — é puro processamento local, seguro de rodar em qualquer aba.
    //
    // Reconcilia quando o CONJUNTO de pedidos muda — não só a quantidade. Antes
    // comparávamos só o TOTAL (length): um pedido preso no cache com status ANTIGO
    // (ex.: PIX em 77/99 que depois foi pago e virou 0 no backend) mantém o mesmo id,
    // então o total não mudava e o card ficava travado exibindo "Não Identificado" na
    // coluna laranja. Comparar a assinatura `id:status` pega mudança de status e pedido
    // fantasma (no cache, mas fora do allday).
    reconciliarAllday: (pedidos: any[]) => {
        try {
            if (get().tabStatus !== TabStatusEnum.CRUD) return
            if (!Array.isArray(pedidos)) return

            const assinatura = (arr: any[]) =>
                (arr || [])
                    .map((p: any) => `${p.id}:${p.status}`)
                    .sort()
                    .join("|")

            // Fonte do que está VISÍVEL: as próprias colunas (arrayTodosPedidos não é
            // atualizado pelos upserts do SSE, então estaria desatualizado). Ignora
            // aceitosMaisPreparacao (é união de aceitos+preparação — duplicaria).
            const visiveis = [
                ...get().pedidosAguardandoAceitacao,
                ...get().pedidosAceitos,
                ...get().pedidosEmPreparacao,
                ...get().pedidosEmEntrega,
                ...get().pedidosFinalizados,
            ]
            const visiveisUnicos = Array.from(
                new Map(visiveis.map((p: any) => [p.id, p])).values()
            )

            if (assinatura(pedidos) !== assinatura(visiveisUnicos)) {
                get().updatePedidosRecebidos({ data: pedidos })
            } else {
                // Rede de segurança do ACEITE (independente do redesenho acima):
                // um pedido pode estar preso em "Aguardando" (status 0) porque o
                // push ao vivo o EXIBIU (evento leve → reconciliarPedido) mas o
                // aceite automático nunca saiu — ou o evento pesado se perdeu, ou o
                // aceite falhou/foi pulado (linha ~821, que retorna contando que "a
                // rede reoferece"). Como a assinatura `id:0` continua igual à da
                // tela, o gate acima pulava o ÚNICO re-aceite e o backend expirava o
                // pedido (-4) mesmo com o painel aberto. Aqui reoferecemos o
                // aceite/preparo dos que seguem em status 0 a cada ciclo. Idempotente:
                // reivindicarAceite (janela 60s) dedup entre abas/estações e o aceite
                // no backend é idempotente, então mesmo que a reivindicação ainda
                // esteja de pé num ciclo, o seguinte reoferece; se já foi aceito no
                // backend, o próximo allday muda a assinatura e cai no redesenho normal.
                get().reofertarAceitePendentes(pedidos)
            }
            // A verificação de impressão roda sempre (neutra para a UI — só imprime e marca).
            get().reimprimirPendentesRecentes()
        } catch (error) {
            RegistrarLogErro(error, "reconciliarAllday")
        }
    },

    // Rede de segurança contra eventos perdidos (SSE/QZ podem cair sem aviso): refaz a
    // consulta allday e reconcilia. Roda a partir de um intervalo GLOBAL no Cabeçalho —
    // antes vivia só no RecebedorDePedidos, então em Mesas/Registrador não havia rede
    // nenhuma e um único push SSE perdido fazia o pedido expirar.
    //
    // O FETCH é feito só pela aba LÍDER (o chamador gateia por `souLiderPedidos`), para
    // não repetir a chamada de rede em cada aba do navegador. Retorna o array do allday
    // para que o chamador repasse às demais abas (que reconciliam sem refazer a rede).
    redeSegurancaPedidos: async () => {
        try {
            // NÃO gateia o FETCH por tabStatus: quem chama é só a aba LÍDER do SSE
            // (OperacaoTempoReal), e a líder PODE não ser a aba do Painel (ex.: um
            // Registrador/Mesas, ou outra aba/PWA que pegou o lock primeiro). Se
            // exigíssemos CRUD aqui, uma líder fora do Painel retornaria null, o allday
            // não era buscado nem repassado, e a aba do Painel (seguidora) nunca
            // reofertava o aceite → o pedido EXPIRAVA (-4) mesmo com aceite automático.
            // O gate por CRUD fica onde a AÇÃO acontece: reconciliarAllday e
            // reofertarAceitePendentes (por aba). A líder busca e repassa; cada aba
            // reconcilia/reoferece só se estiver operando (CRUD).
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return null
            const response = await api.get(`integracao/orders/allday`, {
                headers: { "Content-Type": "application/json", Authorization: autorizacao },
            })
            if (typeof response.data === "string") return null
            if (!Array.isArray(response.data)) return null
            get().reconciliarAllday(response.data)
            return response.data
        } catch (error) {
            RegistrarLogErro(error, "redeSegurancaPedidos")
            return null
        }
    },
})

export default createPedidosStore
